import { Button, ButtonGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { HiOutlineChevronLeft, HiOutlineChevronRight, HiPlus } from "react-icons/hi2";
import type { activityType } from "models/activity.model";
import Activity from "components/activity/activity";
import Loader from "components/loader/loader";
import { forwardRef } from "react";
import "./scrollableSection.scss";
import { images } from 'assets';
import { GoChevronLeft, GoChevronRight } from "react-icons/go";

interface ScrollableSectionProps {
    id: string;
    idx: number;
    title: React.ReactNode;
    isScrollable: boolean;
    isSeeMore: boolean;
    hideSeeMoreButton?: boolean;
    collectionId?: string;
    items?: activityType[];
    onClose?: () => void;
    onSeeMoreToggle?: () => void;
    onLoadMore?: () => void;
    onSnapScroll?: (direction: string, id: string) => void;
    isMobile: boolean;
    isLoading: boolean;
    isLoadMoreButton?: boolean;
    isPagination?: boolean;
    hasPrev?: boolean;
    hasNext?: boolean;
    paginateNext?: () => void;
    paginatePrev?: () => void;
    paginationInfo?: string;
}

export interface sectionDataType {
    sectionId: string,
    sectionName: string,
    activities: activityType[],
    isSeeMore: boolean,
    isScrollable: boolean,
    isLoading: boolean,
    img?: string,
    description?: string,
}

const ScrollableSection = forwardRef<HTMLDivElement, ScrollableSectionProps>(
    ({
        id,
        idx,
        title,
        isScrollable,
        isSeeMore,
        hideSeeMoreButton = false,
        items,
        onClose,
        onSeeMoreToggle,
        onSnapScroll,
        onLoadMore,
        isMobile,
        isLoading,
        isLoadMoreButton,
        isPagination,
        hasPrev,
        hasNext,
        paginateNext,
        paginatePrev,
        paginationInfo,
    }: ScrollableSectionProps,
        ref) => {

        const { t } = useTranslation();

        const activities = items;

        const handleKeyDownLoadMore = (e: React.KeyboardEvent<SVGElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onLoadMore?.()
            }
        };

        const handleKeyDownScroll = (e: React.KeyboardEvent<SVGElement>, side: string, id: string) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSnapScroll?.(side, id)
            }
        };


        return (
            <section
                className={`scrollable_section section-${idx % 10}`}
                aria-labelledby={`section_id_${id}`}>

                <div className="scrollable_section__topline">
                    <h5 id={`section_id_${id}`}>{title}</h5>
                    <div className="scrollable_section__topline__buttons">
                        {!isMobile && isScrollable && (
                            <>
                                <HiOutlineChevronLeft
                                    className="scrollable_section__scrollButton"
                                    tabIndex={0}
                                    role="button"
                                    aria-label={t("scroll_left")}
                                    onClick={() => onSnapScroll?.("left", id)}
                                    onKeyDown={(e) => handleKeyDownScroll(e, "left", id)}
                                />
                                <HiOutlineChevronRight
                                    className="scrollable_section__scrollButton"
                                    tabIndex={0}
                                    role="button"
                                    aria-label={t("scroll_right")}
                                    onClick={() => onSnapScroll?.("right", id)}
                                    onKeyDown={(e) => handleKeyDownScroll(e, "right", id)}
                                />
                            </>
                        )}
                        {(isSeeMore || isScrollable) && !hideSeeMoreButton && (
                            <Button
                                className="scrollable_section__seeMoreButton"
                                variant="primary"
                                size="sm"
                                onClick={onSeeMoreToggle}
                                aria-expanded={isSeeMore}
                                aria-controls={`section__${id}`}
                            >
                                {t("see_more")}<span className="plus">{isSeeMore ? '-' : '+'}</span>
                            </Button>
                        )}
                    </div>
                </div>

                <div ref={ref} id={`section__${id}`} className={`scrollable_section__items ${isSeeMore ? 'seeMore' : ''}`} >

                    {!isLoading && activities?.length === 0 && (
                        <div className="noActivities inCollection">
                            {t("noActivity")}
                        </div>
                    )}

                    {activities !== null &&
                        <ul>
                            {activities?.map((activity: activityType, index: number) => (
                                <Activity key={index} activity={activity} onClose={onClose} />
                            ))}

                            {isLoadMoreButton && (
                                <li className="activity loadMore">
                                    <div className="activity_image">
                                        <span className="activity_tooltip">
                                            <HiPlus className="h-6 w-6" tabIndex={0} role="button" aria-describedby="load_more-instructions" onClick={onLoadMore} onKeyDown={handleKeyDownLoadMore} />
                                        </span>
                                        <img src={images?.['loadMore']} alt="" aria-hidden="true"></img>
                                    </div>
                                    <div className="activity_footer">
                                        <span className="activity_footer__title" id="load_more-instructions">{t("load_more_activities")}</span>
                                    </div>
                                </li>
                            )}

                            {isLoading && (
                                <li><Loader className="loader_mini loader_tertiary" /></li>
                            )}
                        </ul>
                    }

                    {isPagination &&
                        <div className="pagination">
                            <ButtonGroup>
                                {hasPrev && <Button className="btn_primary" onClick={paginatePrev}><GoChevronLeft /></Button>}
                                {hasNext && <Button className="btn_primary" onClick={paginateNext}><GoChevronRight /></Button>}
                            </ButtonGroup>
                            <p>{paginationInfo}</p>
                        </div>
                    }

                </div>
            </section>
        );
    }
);

export default ScrollableSection;